import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { tpAPI, courseAPI, categoryAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Loader2, FlaskConical, BookOpen, Layers, Link2, X } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "@/components/editor/RichTextEditor";

export default function TPEditorPage() {
  const { workspaceId, tpId } = useParams();
  const navigate = useNavigate();
  const isNew = !tpId || tpId === "new";
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [allTPs, setAllTPs] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content: "",
    chapter: "",
    category_id: null,
    order: 0,
    duration_minutes: 60,
    difficulty: "medium",
    attachments: [],
    is_visible: true,
    course_id: null,
    section_id: null,
    related_tps: [], // TPs liés
  });

  useEffect(() => {
    loadCourses();
    loadAllTPs();
    categoryAPI.list(workspaceId).then(res => setCategoriesList(res.data)).catch(() => {});
    if (!isNew && tpId) {
      setLoading(true);
      loadTP();
    }
  }, [tpId, isNew]);

  const loadCourses = async () => {
    try {
      const response = await courseAPI.list(workspaceId);
      setCourses(response.data);
    } catch (error) {
      console.error("Failed to load courses:", error);
    }
  };

  const loadAllTPs = async () => {
    try {
      const response = await tpAPI.list(workspaceId);
      // Filter out current TP if editing
      setAllTPs(response.data.filter(tp => tp.id !== tpId));
    } catch (error) {
      console.error("Failed to load TPs:", error);
    }
  };

  const loadTP = async () => {
    try {
      const response = await tpAPI.get(workspaceId, tpId);
      setFormData({
        title: response.data.title || "",
        description: response.data.description || "",
        content: response.data.content || "",
        chapter: response.data.chapter || "",
        category_id: response.data.category_id || null,
        order: response.data.order || 0,
        duration_minutes: response.data.duration_minutes || 60,
        difficulty: response.data.difficulty || "medium",
        attachments: response.data.attachments || [],
        is_visible: response.data.is_visible !== false,
        course_id: response.data.course_id || null,
        section_id: response.data.section_id || null,
        related_tps: response.data.related_tps || [],
      });
    } catch (error) {
      console.error("Failed to load TP:", error);
      toast.error("Erreur lors du chargement du TP");
      navigate(`/workspace/${workspaceId}/tp`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await tpAPI.create(workspaceId, formData);
        toast.success("TP créé avec succès");
      } else {
        await tpAPI.update(workspaceId, tpId, formData);
        toast.success("TP mis à jour");
      }
      navigate(`/workspace/${workspaceId}/tp`);
    } catch (error) {
      console.error("Failed to save TP:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/workspace/${workspaceId}/tp`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {isNew ? "Nouveau TP" : "Modifier le TP"}
            </h1>
            <p className="text-muted-foreground">
              {isNew ? "Créez un nouveau travail pratique" : "Modifiez le contenu du TP"}
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-orange-600" />
                  Informations générales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Installation et configuration de Docker"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brève description du TP..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Content Editor */}
            <Card>
              <CardHeader>
                <CardTitle>Contenu du TP</CardTitle>
              </CardHeader>
              <CardContent>
                <RichTextEditor
                  content={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Rédigez les instructions du TP ici..."
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Attachment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Rattachement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Rattacher à un cours</Label>
                  <Select
                    value={formData.course_id || "none"}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      course_id: value === "none" ? null : value,
                      section_id: value === "none" ? null : formData.section_id
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Aucun cours" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun cours</SelectItem>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.course_id && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Rattacher à une section
                    </Label>
                    <Select
                      value={formData.section_id || "none"}
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        section_id: value === "none" ? null : value 
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Aucune section" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Directement au cours</SelectItem>
                        {courses
                          .find(c => c.id === formData.course_id)
                          ?.sections?.map((section, index) => (
                            <SelectItem key={section.id} value={section.id}>
                              Section {index + 1}: {section.title}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Related TPs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  TPs liés
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Ajouter un TP lié</Label>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      if (value && !formData.related_tps.includes(value)) {
                        setFormData({ 
                          ...formData, 
                          related_tps: [...formData.related_tps, value]
                        });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un TP" />
                    </SelectTrigger>
                    <SelectContent>
                      {allTPs
                        .filter(tp => !formData.related_tps.includes(tp.id))
                        .map((tp) => (
                          <SelectItem key={tp.id} value={tp.id}>
                            {tp.title}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.related_tps.length > 0 && (
                  <div className="space-y-2">
                    <Label>TPs liés sélectionnés</Label>
                    <div className="space-y-2">
                      {formData.related_tps.map((tpId) => {
                        const relatedTP = allTPs.find(tp => tp.id === tpId);
                        return (
                          <div 
                            key={tpId} 
                            className="flex items-center justify-between p-2 bg-muted rounded"
                          >
                            <span className="text-sm truncate flex-1">
                              {relatedTP?.title || tpId}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setFormData({
                                ...formData,
                                related_tps: formData.related_tps.filter(id => id !== tpId)
                              })}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  Les TPs liés seront affichés comme suggestions à la fin du TP.
                </p>
              </CardContent>
            </Card>

            {/* Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Paramètres</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="chapter">Chapitre</Label>
                  <Input
                    id="chapter"
                    value={formData.chapter}
                    onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                    placeholder="Ex: Module 1 - Introduction"
                  />
                </div>
                {categoriesList.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="tp-category">Catégorie</Label>
                    <select
                      id="tp-category"
                      value={formData.category_id || ""}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="tp-category-select"
                    >
                      <option value="">Sans catégorie</option>
                      {categoriesList.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="order">Ordre d'affichage</Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Durée estimée (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Difficulté</Label>
                  <Select
                    value={formData.difficulty}
                    onValueChange={(value) => setFormData({ ...formData, difficulty: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Facile</SelectItem>
                      <SelectItem value="medium">Moyen</SelectItem>
                      <SelectItem value="hard">Difficile</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Visibility */}
            <Card>
              <CardHeader>
                <CardTitle>Visibilité</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Visible pour les candidats</Label>
                    <p className="text-sm text-muted-foreground">
                      {formData.is_visible 
                        ? "Les candidats peuvent voir ce TP" 
                        : "Ce TP est caché aux candidats"}
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_visible: checked })}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
